'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { toast, Toaster } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface Fornitore {
  id: string;
  ragione_sociale: string;
  partita_iva: string;
  indirizzo: string;
  citta: string;
  telefono: string;
  email: string;
  sito_web: string;
  referente_nome: string;
  referente_telefono: string;
  referente_email: string;
  sla_ore_intervento: number;
  sla_ore_risposta: number;
  orari_reperibilita: string;
  note_contrattuali: string;
  is_active: boolean;
}

const emptyFornitore: Omit<Fornitore, 'id'> = {
  ragione_sociale: '', partita_iva: '', indirizzo: '', citta: '',
  telefono: '', email: '', sito_web: '',
  referente_nome: '', referente_telefono: '', referente_email: '',
  sla_ore_intervento: 8, sla_ore_risposta: 4,
  orari_reperibilita: '', note_contrattuali: '', is_active: true,
};

export default function FornitoriContent() {
  const [fornitori, setFornitori] = useState<Fornitore[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<Fornitore | null>(null);
  const [formData, setFormData] = useState<Omit<Fornitore, 'id'>>({ ...emptyFornitore });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string }>({ open: false, id: '', name: '' });
  const [selectedFornitore, setSelectedFornitore] = useState<Fornitore | null>(null);
  const { canWrite } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('fornitori').select('*').order('ragione_sociale');
      if (error) throw error;
      setFornitori(data || []);
    } catch (err: any) {
      toast.error('Errore caricamento: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = fornitori.filter(f =>
    f.ragione_sociale.toLowerCase().includes(search.toLowerCase()) ||
    f.referente_nome.toLowerCase().includes(search.toLowerCase()) ||
    f.email.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditItem(null);
    setFormData({ ...emptyFornitore });
    setShowForm(true);
  };

  const openEdit = (f: Fornitore) => {
    setEditItem(f);
    setFormData({ ...f });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.ragione_sociale.trim()) { toast.error('Ragione sociale obbligatoria'); return; }
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) { toast.error('Formato email non valido'); return; }
    if (formData.referente_email && !emailRegex.test(formData.referente_email)) { toast.error('Formato email referente non valido'); return; }
    // P.IVA validation (Italian format: 11 digits)
    if (formData.partita_iva && !/^\d{11}$/.test(formData.partita_iva.replace(/\s/g, ''))) {
      toast.error('P.IVA deve essere di 11 cifre');
      return;
    }
    // SLA validation
    if (formData.sla_ore_risposta <= 0) { toast.error('SLA ore risposta deve essere > 0'); return; }
    if (formData.sla_ore_intervento <= 0) { toast.error('SLA ore intervento deve essere > 0'); return; }
    setSaving(true);
    try {
      if (editItem) {
        const { error } = await supabase.from('fornitori').update({ ...formData, updated_at: new Date().toISOString() }).eq('id', editItem.id);
        if (error) throw error;
        toast.success('Fornitore aggiornato');
      } else {
        const { error } = await supabase.from('fornitori').insert(formData);
        if (error) throw error;
        toast.success('Fornitore creato');
      }
      setShowForm(false);
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase.from('fornitori').delete().eq('id', deleteConfirm.id);
      if (error) throw error;
      toast.success('Fornitore eliminato');
      setDeleteConfirm({ open: false, id: '', name: '' });
      if (selectedFornitore?.id === deleteConfirm.id) setSelectedFornitore(null);
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const slaColor = (ore: number) => ore <= 4 ? 'text-emerald-400' : ore <= 8 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="flex flex-col h-full bg-background">
      <Toaster richColors position="top-right" />
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-surface">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestione Fornitori</h1>
          <p className="text-sm text-muted-color mt-0.5">Anagrafica, SLA, contatti e note contrattuali</p>
        </div>
        {canWrite() && (
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg font-semibold transition-colors">
            <Icon name="PlusIcon" size={18} /> Nuovo Fornitore
          </button>
        )}
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* List */}
        <div className="w-96 border-r border-border flex flex-col overflow-hidden">
          <div className="p-4 border-b border-border">
            <div className="relative">
              <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-color" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca fornitore..."
                className="w-full bg-surface-elevated border border-border rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-muted-color focus:outline-none focus:border-sky-500" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-6 h-6 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-muted-color">
                <Icon name="BuildingStorefrontIcon" size={32} className="mb-2 opacity-30" />
                <p className="text-sm">Nessun fornitore trovato</p>
              </div>
            ) : (
              filtered.map(f => (
                <button key={f.id} onClick={() => setSelectedFornitore(f)}
                  className={`w-full flex items-start gap-3 p-4 border-b border-border hover:bg-surface-elevated transition-colors text-left ${selectedFornitore?.id === f.id ? 'bg-sky-500/10 border-l-2 border-l-sky-500' : ''}`}>
                  <div className="w-10 h-10 rounded-lg bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="BuildingStorefrontIcon" size={20} className="text-sky-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-white text-sm truncate">{f.ragione_sociale}</p>
                    <p className="text-xs text-muted-color truncate">{f.referente_nome || f.email || f.citta}</p>
                    <div className="flex gap-3 mt-1">
                      <span className={`text-xs font-semibold ${slaColor(f.sla_ore_risposta)}`}>Risposta: {f.sla_ore_risposta}h</span>
                      <span className={`text-xs font-semibold ${slaColor(f.sla_ore_intervento)}`}>Intervento: {f.sla_ore_intervento}h</span>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${f.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                    {f.is_active ? 'Attivo' : 'Inattivo'}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Detail */}
        <div className="flex-1 overflow-y-auto p-6">
          {!selectedFornitore ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-color">
              <Icon name="BuildingStorefrontIcon" size={64} className="mb-4 opacity-20" />
              <p className="text-xl font-semibold">Seleziona un fornitore</p>
              <p className="text-sm mt-1">Clicca su un fornitore per vedere i dettagli</p>
            </div>
          ) : (
            <div className="max-w-3xl">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white">{selectedFornitore.ragione_sociale}</h2>
                  {selectedFornitore.partita_iva && <p className="text-sm text-muted-color mt-0.5">P.IVA: {selectedFornitore.partita_iva}</p>}
                </div>
                {canWrite() && (
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(selectedFornitore)} className="flex items-center gap-2 px-3 py-2 bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 rounded-lg font-semibold text-sm transition-colors">
                      <Icon name="PencilIcon" size={14} /> Modifica
                    </button>
                    <button onClick={() => setDeleteConfirm({ open: true, id: selectedFornitore.id, name: selectedFornitore.ragione_sociale })}
                      className="flex items-center gap-2 px-3 py-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-lg font-semibold text-sm transition-colors">
                      <Icon name="TrashIcon" size={14} /> Elimina
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Contatti */}
                <div className="bg-surface border border-border rounded-xl p-5">
                  <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                    <Icon name="PhoneIcon" size={16} className="text-sky-400" /> Contatti Aziendali
                  </h3>
                  <div className="space-y-2 text-sm">
                    {selectedFornitore.indirizzo && <p><span className="text-muted-color">Indirizzo:</span> <span className="text-white">{selectedFornitore.indirizzo}, {selectedFornitore.citta}</span></p>}
                    {selectedFornitore.telefono && <p><span className="text-muted-color">Tel:</span> <span className="text-white">{selectedFornitore.telefono}</span></p>}
                    {selectedFornitore.email && <p><span className="text-muted-color">Email:</span> <a href={`mailto:${selectedFornitore.email}`} className="text-sky-400 hover:underline">{selectedFornitore.email}</a></p>}
                    {selectedFornitore.sito_web && <p><span className="text-muted-color">Web:</span> <a href={selectedFornitore.sito_web} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">{selectedFornitore.sito_web}</a></p>}
                  </div>
                </div>

                {/* Referente */}
                <div className="bg-surface border border-border rounded-xl p-5">
                  <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                    <Icon name="UserIcon" size={16} className="text-sky-400" /> Referente Tecnico
                  </h3>
                  <div className="space-y-2 text-sm">
                    {selectedFornitore.referente_nome && <p><span className="text-muted-color">Nome:</span> <span className="text-white">{selectedFornitore.referente_nome}</span></p>}
                    {selectedFornitore.referente_telefono && <p><span className="text-muted-color">Tel:</span> <span className="text-white">{selectedFornitore.referente_telefono}</span></p>}
                    {selectedFornitore.referente_email && <p><span className="text-muted-color">Email:</span> <a href={`mailto:${selectedFornitore.referente_email}`} className="text-sky-400 hover:underline">{selectedFornitore.referente_email}</a></p>}
                  </div>
                </div>

                {/* SLA */}
                <div className="bg-surface border border-border rounded-xl p-5">
                  <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                    <Icon name="ClockIcon" size={16} className="text-sky-400" /> SLA e Reperibilità
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-color">Tempo risposta</span>
                      <span className={`text-lg font-bold ${slaColor(selectedFornitore.sla_ore_risposta)}`}>{selectedFornitore.sla_ore_risposta}h</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-color">Tempo intervento</span>
                      <span className={`text-lg font-bold ${slaColor(selectedFornitore.sla_ore_intervento)}`}>{selectedFornitore.sla_ore_intervento}h</span>
                    </div>
                    {selectedFornitore.orari_reperibilita && (
                      <div>
                        <p className="text-sm text-muted-color mb-1">Orari reperibilità</p>
                        <p className="text-sm text-white bg-surface-elevated rounded-lg p-2">{selectedFornitore.orari_reperibilita}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Note contrattuali */}
                {selectedFornitore.note_contrattuali && (
                  <div className="bg-surface border border-border rounded-xl p-5">
                    <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                      <Icon name="DocumentTextIcon" size={16} className="text-sky-400" /> Note Contrattuali
                    </h3>
                    <p className="text-sm text-secondary-color leading-relaxed">{selectedFornitore.note_contrattuali}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-xl font-bold text-white">{editItem ? 'Modifica' : 'Nuovo'} Fornitore</h2>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-lg hover:bg-surface-elevated text-muted-color"><Icon name="XMarkIcon" size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Ragione Sociale *</label>
                  <input value={formData.ragione_sociale} onChange={e => setFormData({ ...formData, ragione_sociale: e.target.value })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                    placeholder="Nome azienda" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Partita IVA</label>
                  <input value={formData.partita_iva} onChange={e => setFormData({ ...formData, partita_iva: e.target.value })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                    placeholder="01234567890" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Città</label>
                  <input value={formData.citta} onChange={e => setFormData({ ...formData, citta: e.target.value })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                    placeholder="Milano" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Telefono</label>
                  <input value={formData.telefono} onChange={e => setFormData({ ...formData, telefono: e.target.value })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                    placeholder="02-12345678" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Email</label>
                  <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                    placeholder="info@fornitore.it" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Indirizzo</label>
                  <input value={formData.indirizzo} onChange={e => setFormData({ ...formData, indirizzo: e.target.value })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                    placeholder="Via Roma 1" />
                </div>
              </div>
              <div className="border-t border-border pt-4">
                <p className="text-sm font-bold text-white mb-3">Referente Tecnico</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-1.5">Nome Referente</label>
                    <input value={formData.referente_nome} onChange={e => setFormData({ ...formData, referente_nome: e.target.value })}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                      placeholder="Nome Cognome" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-1.5">Tel. Referente</label>
                    <input value={formData.referente_telefono} onChange={e => setFormData({ ...formData, referente_telefono: e.target.value })}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                      placeholder="333-1234567" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-secondary-color mb-1.5">Email Referente</label>
                    <input type="email" value={formData.referente_email} onChange={e => setFormData({ ...formData, referente_email: e.target.value })}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                      placeholder="referente@fornitore.it" />
                  </div>
                </div>
              </div>
              <div className="border-t border-border pt-4">
                <p className="text-sm font-bold text-white mb-3">SLA e Reperibilità</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-1.5">SLA Risposta (ore)</label>
                    <input type="number" min={1} value={formData.sla_ore_risposta} onChange={e => setFormData({ ...formData, sla_ore_risposta: parseInt(e.target.value) || 4 })}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-1.5">SLA Intervento (ore)</label>
                    <input type="number" min={1} value={formData.sla_ore_intervento} onChange={e => setFormData({ ...formData, sla_ore_intervento: parseInt(e.target.value) || 8 })}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-secondary-color mb-1.5">Orari Reperibilità</label>
                    <input value={formData.orari_reperibilita} onChange={e => setFormData({ ...formData, orari_reperibilita: e.target.value })}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                      placeholder="Lun-Ven 8-18, H24 per critici" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Note Contrattuali</label>
                <textarea value={formData.note_contrattuali} onChange={e => setFormData({ ...formData, note_contrattuali: e.target.value })} rows={3}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500 resize-none"
                  placeholder="Note su contratto, condizioni speciali..." />
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-border">
              <button onClick={() => setShowForm(false)} className="flex-1 px-4 py-2.5 border border-border rounded-lg text-secondary-color hover:bg-surface-elevated font-semibold transition-colors">Annulla</button>
              <button onClick={handleSave} disabled={saving || !formData.ragione_sociale}
                className="flex-1 px-4 py-2.5 bg-sky-500 hover:bg-sky-600 disabled:opacity-50 text-white rounded-lg font-semibold transition-colors">
                {saving ? 'Salvataggio...' : editItem ? 'Aggiorna' : 'Crea'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Elimina Fornitore"
        message={`Sei sicuro di voler eliminare "${deleteConfirm.name}"?`}
        confirmLabel="Elimina"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ open: false, id: '', name: '' })}
      />
    </div>
  );
}
